import { useMenuContext } from '#pplx/frontend/aether/components/internal/menu/useMenuContext.js';
import { useCallback, useEffect, useId, useRef } from 'react';
/**
 * Hook for managing a registry of submenus.
 *
 * This hook maintains a map of submenu IDs to their close functions,
 * allowing parent menus to close all their direct child submenus.
 *
 * @example
 * ```tsx
 * const submenus = useMenuSubmenuRegistry();
 *
 * // Register a submenu
 * submenus.register('submenu-1', () => setIsOpen(false));
 *
 * // Close all submenus (e.g., on parent scroll)
 * submenus.closeAll();
 *
 * // Unregister when unmounting
 * submenus.unregister('submenu-1');
 * ```
 */
export function useMenuSubmenuRegistry() {
    const registryRef = useRef(new Map());
    const register = useCallback((id, closeFn) => {
        registryRef.current.set(id, closeFn);
    }, []);
    const unregister = useCallback((id) => {
        registryRef.current.delete(id);
    }, []);
    const closeAll = useCallback(() => {
        registryRef.current.forEach((closeFn) => closeFn());
    }, []);
    const closeOthers = useCallback((exceptId) => {
        registryRef.current.forEach((closeFn, id) => {
            if (id !== exceptId) {
                closeFn();
            }
        });
    }, []);
    return {
        closeAll,
        closeOthers,
        register,
        unregister,
    };
}
/**
 * Hook for registering a submenu with its parent menu context.
 *
 * This hook automatically:
 * - Generates a unique ID for the submenu
 * - Retrieves the parent menu context
 * - Registers the submenu's close function with the parent menu's registry on mount
 * - Unregisters it on unmount
 * - Returns a callback that closes the submenu's registered siblings
 *
 * This allows parent menus to close all direct child submenus (e.g., when
 * scrolling) and individual submenus to close their siblings before opening.
 *
 * @param closeSubmenu - Function to call to close the submenu
 *
 * @example
 * ```tsx
 * function MenuSubmenu() {
 *   const closeSubmenu = useCallback(() => setIsOpen(false), []);
 *   useMenuSubmenuRegistration(closeSubmenu);
 *
 *   // ... rest of submenu logic
 * }
 * ```
 */
export function useMenuSubmenuRegistration(closeSubmenu) {
    const context = useMenuContext();
    const submenuId = useId();
    useEffect(() => {
        if (!context)
            return;
        context.submenus.register(submenuId, closeSubmenu);
        return () => {
            context.submenus.unregister(submenuId);
        };
    }, [context, submenuId, closeSubmenu]);
    return useCallback(() => {
        context?.submenus.closeOthers(submenuId);
    }, [context, submenuId]);
}
//# sourceMappingURL=useMenuSubmenuRegistry.js.map